import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from "rxjs/operators";
import {SelectItem} from 'primeng/api';

import {BLOB_RESPONSE_TYPE} from "@app/shared/classes/common";
import {OrgType, PartnerAddress, PartnerContact, PaymentTerm, SalesTaxRate, TradingPartner, TradingPartnerSummary} from "@app/shared/classes/trading-partner";
import {SearchCorrespondence} from '@app/shared/classes/correspondence';
import {PartnerPaymentSetting} from "@app/shared/classes/partner-payment-setting";
import {QueryResult} from "@app/shared/classes/query-result";
import {ApprovalStatusType} from "@app/shared/enums/approval-status-type.enum";
import {TradingPartnerType} from "@app/shared/enums/trading-partner-type.enum";
import {CommonTransformationService} from '@app/shared/services/common-transformation.service';
import {SearchTransactionSummary} from '../org-admin/org-summary/org-summary.component';
import {Container, ToDo} from "@app/shared/classes/to-do";
import {AchMicroDepositVerification, OrgAchDetail, PartnerAchDetail, PartnerPaymentTerms} from "@app/shared";

@Injectable()
export class TradingPartnerService {
  constructor(private http: HttpClient, private transform: CommonTransformationService) {
  }

  findLocation(locationId) {
    let locationSearch = `/directory/locations`;
    return this.http.get<any>(`${locationSearch}/${locationId}`);
  }

  searchPartners(businessName: string, tradingPartnerType?: TradingPartnerType, currentStatus?: ApprovalStatusType): Observable<QueryResult> {
    let url = `/procurement/trading-partners/search?businessName=${businessName}`;
    if (tradingPartnerType) {
      url += `&subtype=${tradingPartnerType}`;
    }
    if (currentStatus) {
      url += `&currentStatus=${currentStatus}`;
    } else {
      url += `&currentStatus=${ApprovalStatusType.Completed}`;
    }
    return this.http.get<QueryResult>(url);
  }

  getPartners(partnerType?: TradingPartnerType, currentStatus?: ApprovalStatusType): Observable<QueryResult> {
    let url = `/procurement/trading-partners/search?count=-1`;
    if (partnerType) {
      url += `&subtype=${partnerType}`;
    }
    if (currentStatus) {
      url += `&currentStatus=${currentStatus}`;
    } else {
      url += `&currentStatus=${ApprovalStatusType.Completed}`;
    }
    return this.http.get<QueryResult>(url);
  }

  getPartnersAsDropdown(partnerType?: TradingPartnerType, currentStatus?: ApprovalStatusType): Observable<SelectItem[]> {
    return this.getPartners(partnerType, currentStatus).pipe(map(res => {
      return this.transform.convertToDropDown("businessName", res.list, "id", true);
    }));
  }

  getPartnerSummaryById(partnerId: number): Observable<TradingPartnerSummary> {
    const url = `/procurement/trading-partners/summary/${partnerId}`;
    return this.http.get<TradingPartnerSummary>(url);
  }

  /**
   * @desc returns matching trading partner info
   * @param active - active trading partner
   * @param currentStatus - COMPLETED => approved trading partner
   */
  searchPartnerInfo(businessName: string, subtype?: TradingPartnerType, active?: boolean, currentStatus?: string): Observable<QueryResult> {
    let url = `/procurement/trading-partners/info/search?`;

    if (active != null) {
      url += `active=${active}&`;
    } else {
      url += `active=true&`;                    // get only active trading partner
    }
    if (subtype) {
      url += `subtype=${subtype}&`;
    }
    if (currentStatus) {
      url += `currentStatus=${currentStatus}&`;
    } else {
      url += `currentStatus=COMPLETED&`;         // get only approved trading partner
    }
    if (businessName) {
      url += `businessName=${businessName}`;
    }

    return this.http.get<QueryResult>(url);
  }

  searchPartnerInfoAsDropdown(businessName: string, subtype?: TradingPartnerType, active?: boolean, currentStatus?: string): Observable<SelectItem[]> {
    return this.searchPartnerInfo(businessName, subtype, active, currentStatus).pipe(map(res => {
      return this.transform.convertToDropDown("businessName", res.list, "id");
    }));
  }

  searchPartnerSummary(businessName: string, active: boolean, currentStatus?: string, subtype?: string): Observable<QueryResult> {
    let url = `/procurement/trading-partners/summary?`;

    if (active != null) {
      url += `active=${active}&`;
    }
    if (businessName) {
      url += `businessName=${businessName}&`;
    }
    if (currentStatus) {
      url += `currentStatus=${currentStatus}&`;
    }
    if (subtype) {
      url += `subtype=${subtype}`;
    }

    return this.http.get<QueryResult>(url);
  }

  // PartnerSummary is equivalent to PartnerInfo
  searchPartnerSummaryAsDropdown(businessName: string, active: boolean, currentStatus?: string, subtype?: string): Observable<any> {
    return this.searchPartnerSummary(businessName, active, currentStatus, subtype).pipe(map(res => {
      return this.transform.convertToDropDown("businessName", res.list, "id");
    }));
  }

  searchPartnerTransSummary(businessName: string, orgType?: string): Observable<QueryResult> {
    let url = `/procurement/trading-partners/transaction/summary?`;
    if (businessName) {
      url += `businessName=${businessName}&`;
    }
    if (orgType) {
      url += `subtype=${orgType}`;
    }

    return this.http.get<QueryResult>(url);
  }

  getTradingPartnerSummary(orgType: string, active?: boolean): Observable<QueryResult> {
    let url = `/procurement/trading-partners/summary?subtype=${orgType}&count=-1`;

    if (active) {
      url += `&active=true`;
    }
    return this.http.get<QueryResult>(url);
  }

  getTransactionTypes(name: string, orgType: OrgType): Observable<SelectItem[]> {
    const url = `/utils/enum-pairs?name=${name}&subtype=${orgType}&start=0&count=-1`;
    return this.http.get<any>(url).pipe(map(res => {
      return this.transform.convertToDropDown("name", res, "id");
    }));
  }

  //getting transaction summary line items
  getTransactionSummary(orgType: string, active?: boolean): Observable<QueryResult> {
    let url = `/procurement/trading-partners/transaction/summary?subtype=${orgType}&count=-1`;

    if (active) {
      url += `&active=true`;
    }
    return this.http.get<QueryResult>(url);
  }

  searchTransaction(orgType: string, searchTxn: SearchTransactionSummary): Observable<QueryResult> {
    let query: string = this.buildSearchURL(searchTxn);
    return this.http.get<QueryResult>('/procurement/trading-partners/transaction/summary?subtype=' + orgType + query);
  }

  buildSearchURL(searchTxn): string {
    let result: string = '';
    for (let key in searchTxn) {
      let value = searchTxn[key];
      if (value != null) {
        result = result.concat(`&${key}=${value}`);
      }
    }
    return result;
  }

  searchPanelCorrespondence(searchCorrespondence: SearchCorrespondence, partnerId: number): Observable<QueryResult> {
    let query: string = this.buildSearchCorrespondenceURL(searchCorrespondence);
    return this.http.get<QueryResult>('/procurement/trading-partners/correspondences/search?contact.partner.id=' + partnerId + query);
  }

  buildSearchCorrespondenceURL(searchCorrespondence): string {
    let result: string = '';
    for (let key in searchCorrespondence) {
      let value = searchCorrespondence[key];
      console.log(value);
      if (key != null) {
        if (key === 'categoryId') {
          result = result.concat(`&correspondCategory.id=${value}`);
        }
        if (key === 'contactId') {
          result = result.concat(`&contact.id=${value}`);
        }
        if (key === 'displayModifiedOn') {
          result = result.concat(`&modifiedOn=${value}`);
        }
        // result = result.concat(`&${key}=${value}`);
      }
    }
    return result;
  }

  checkPartnerMainContactUniqueness(partnerId: number): Observable<any> {
    let url = `/contact/external/main/unique`;
    return this.http.get<any>(`${url}?partnerId=${partnerId}`);
  }

  checkAddressNameUniqueness(partnerId: number, addrType: string, addrName: string): Observable<any> {
    let url = `/procurement/trading-partners/addresses/unique`;
    return this.http.get<any>(`${url}?partnerId=${partnerId}&addrType=${addrType}&addrName=${addrName}`);
  }

  // /clicking on existing partners to get their profile
  getPartner(partnerId: number): Observable<TradingPartner> {
    const url = `/procurement/trading-partners/${partnerId}`;
    return this.http.get<TradingPartner>(url);
  }

  saveTradingPartner(tp): Observable<TradingPartner> {
    let selectPartnerURL = `/procurement/trading-partners`;
    return this.http.post<TradingPartner>(`${selectPartnerURL}`, tp);
  }

  updateTradingPartner(tp, partnerId: number): Observable<TradingPartner> {
    let selectPartnerURL = `/procurement/trading-partners`;
    return this.http.put<TradingPartner>(`${selectPartnerURL}/${partnerId}`, tp);
  }

  deleteTradingPartner(partnerId: number): Observable<Response> {
    let selectPartnerURL = `/procurement/trading-partners`;
    return this.http.delete<Response>(`${selectPartnerURL}/${partnerId}`);
  }

  submitTradingPartner(tp: TradingPartner): Observable<TradingPartner> {
    let url;
    if (tp.subtype == TradingPartnerType.Vendor) {
      url = "/approvals/vendors";
    } else if (tp.subtype == TradingPartnerType.Customer) {
      url = "/approvals/customers";
    } else {
      console.error("Cannot submit the trading partner because subtype is unknown");
      console.log(tp);
    }
    return this.http.post<TradingPartner>(url, tp);
  }

  resetPartnerStatusToSaved(partnerId: number, partnerType: TradingPartnerType): Observable<any> {
    let url: string;
    if (partnerType == TradingPartnerType.Vendor) {
      url = `/approvals/vendors/reset-saved-state/${partnerId}`;
    } else if (partnerType == TradingPartnerType.Customer) {
      url = `/approvals/customers/reset-saved-state/${partnerId}`;
    } else {
      console.error("Sub type is not recognized");
    }
    return this.http.post<any>(`${url}`, {});
  }

  //additional Info endpoint
  getAdditionalInfo(orgType: string, partnerId?: number): Observable<SelectItem[]> {
    let additionalInfoURL = `/utils/look-up/additional-info?transaction=${orgType}`;
    if (partnerId) {
      additionalInfoURL += `&partnerId=${partnerId}`;
    }
    return this.http.get<SelectItem[]>(additionalInfoURL);
  }

  //to get payment terms
  getPaymentTerms(): Observable<QueryResult> {
    let paymentTermsURL = `/procurement/trading-partners/payment-terms`;
    return this.http.get<QueryResult>(`${paymentTermsURL}`);
  }

  //save payment terms
  savePaymentTerms(paymentTerms): Observable<any> {
    let paymentTermsURL = `/procurement/trading-partners/payment-terms`;
    return this.http.post<any>(`${paymentTermsURL}`, paymentTerms);
  }

  //update payment terms
  updatePaymentTerms(paymentTerms, termId: number): Observable<any> {
    let paymentTermsURL = `/procurement/trading-partners/payment-terms`;
    return this.http.put<any>(`${paymentTermsURL}/${termId}`, paymentTerms);
  }
  //save or update payment terms
  saveOrUpdatePaymentTerm(paymentTerm: PaymentTerm){
    if(paymentTerm.id){
      let url = `/procurement/trading-partners/payment-terms/${paymentTerm.id}`;
      return this.http.put<any>(url, paymentTerm);
    } else {
      let url = `/procurement/trading-partners/payment-terms`;
      return this.http.post<any>(url, paymentTerm);
    }
  }

  //compliance schedule for sales tax agency
  getComplianceSchedule(): Observable<QueryResult> {
    let complianceSchedule = `/core/compliance-deadlines/search`;
    return this.http.get<QueryResult>(`${complianceSchedule}`);
  }

  // search sales tax rate info
  searchSalesTaxRate(name: string): Observable<any> {
    let salesTaxRateURL = `/procurement/sales-taxes/search`;
    return this.http.get<any>(`${salesTaxRateURL}?name=${name}`);
  }

  // search sales tax rate info
  getNearestSalesTaxRateByName(name: string): Observable<any> {
    let salesTaxRateURL = `/procurement/sales-taxes/name`;
    return this.http.get<any>(`${salesTaxRateURL}?name=${name}`);
  }

  saveSalesTaxRate(rateSet, orgId: number): Observable<SalesTaxRate> {
    console.log(orgId);
    if (orgId > 0) {
      console.log("from org..");
      let salesTaxURL = `/solution/sales-taxes`;
      return this.http.post<SalesTaxRate>(`${salesTaxURL}?orgId=${orgId}`, rateSet);
    } else {
      console.log("from customer..");
      let salesTaxURL = `/procurement/sales-taxes`;
      return this.http.post<SalesTaxRate>(`${salesTaxURL}`, rateSet);
    }
  }

  updateSalesTaxRate(rateId: number, rateSet, orgId: number): Observable<SalesTaxRate> {
    console.log(orgId);
    if (orgId > 0) {
      console.log("from org..");
      let salesTaxURL = `/solution/sales-taxes`;
      return this.http.post<SalesTaxRate>(`${salesTaxURL}?orgId=${orgId}`, rateSet);
    } else {
      console.log("from customer..");
      let salesTaxURL = `/procurement/sales-taxes`;
      return this.http.put<SalesTaxRate>(`${salesTaxURL}/${rateId}`, rateSet);
    }
  }

  //searching correspondence categories
  getCorrespondenceCategories(): Observable<QueryResult> {
    let searchCorrespondenceCategories = `/procurement/trading-partners/correspondence-categories/search`;
    return this.http.get<QueryResult>(`${searchCorrespondenceCategories}`);
  }

  //get correspondences
  getCorrespondences(partnerId: number): Observable<QueryResult> {
    let searchCorrespondenceURL = `/procurement/trading-partners/correspondences/search`;
    return this.http.get<QueryResult>(`${searchCorrespondenceURL}?contact.partner.id=${partnerId}&count=-1`);
  }

  //get lanes for org
  getLanes(entityType: string): Observable<QueryResult> {
    let lanesURL = `/todo/tasks/lanes`;
    return this.http.get<QueryResult>(`${lanesURL}?entityType=${entityType}`);
  }

  updateLanes(lanes): Observable<Container[]> {
    let lanesURL = `/todo/tasks/lanes`;
    return this.http.put<Container[]>(`${lanesURL}`, lanes);
  }

  deleteLane(id: number): Observable<any> {
    let lanesURL = `/todo/tasks/lanes`;
    return this.http.delete<any>(`${lanesURL}/${id}`);
  }

  saveLane(container, entityType): Observable<any> {
    let lanesURL = `/todo/tasks/lanes`;
    return this.http.post<any>(`${lanesURL}?transaction=${entityType}`, container);
  }

  deleteCorrespondence(correspondenceId: number): Observable<any> {
    let manipulateCorrespondence = `/procurement/trading-partners/correspondences`;
    return this.http.delete<any>(`${manipulateCorrespondence}/${correspondenceId}`);
  }

  saveCorrespondence(correspondence): Observable<any> {
    let manipulateCorrespondence = `/procurement/trading-partners/correspondences`;
    return this.http.post<any>(`${manipulateCorrespondence}`, correspondence);
  }

  updateCorrespondence(correspondence, id: number): Observable<any> {
    let manipulateCorrespondence = `/procurement/trading-partners/correspondences`;
    return this.http.put<any>(`${manipulateCorrespondence}/${id}`, correspondence);
  }

  getTask(entityType: string, id: number): Observable<QueryResult> {
    let taskURL = `/todo/tasks`;
    return this.http.get<QueryResult>(`${taskURL}?entityType=${entityType}&id=${id}`);
  }

  addTask(widget): Observable<any> {
    let taskURL = `/todo/tasks`;
    return this.http.post<any>(`${taskURL}`, widget);
  }

  updateTasks(widgets): Observable<ToDo[]> {
    let taskURL = `/todo/tasks`;
    return this.http.put<ToDo[]>(`${taskURL}`, widgets);
  }

  updateOneTask(widget, widgetId: number): Observable<any> {
    let taskURL = `/todo/tasks`;
    return this.http.put<any>(`${taskURL}/${widgetId}`, widget);
  }

  deleteTask(widgetId: number): Observable<any> {
    let taskURL = `/todo/tasks`;
    return this.http.delete<any>(`${taskURL}/${widgetId}`);
  }

  getAllPartnerAddresses(): Observable<any> {
    const url = '/procurement/trading-partners/addresses/search';
    return this.http.get<any>(url);
  }

  getAddressByPartnerId(partnerId: number): Observable<QueryResult> {
    const url = `/procurement/trading-partners/addresses/search?partner.id=${partnerId}`;
    return this.http.get<QueryResult>(url);
  }

  getPartnerAddressAsDropdown(customerId: number, addrType?: string, idAsValue?: boolean): Observable<SelectItem<PartnerAddress | number>[]> {
    let url = `/procurement/trading-partners/addresses/search?count=-1&partner.id=${customerId}`;
    if (addrType) {
      url += `&addrType=${addrType}`;
    }
    if (idAsValue) {
      return this.http.get<QueryResult>(url).pipe(map(res => {
        return <SelectItem<number>[]>this.transform.convertToDropDown("addrName", res.list, "id", true);
      }));
    } else {
      return this.http.get<QueryResult>(url).pipe(map(res => {
        return <SelectItem<PartnerAddress>[]>this.transform.convertToDropDown("addrName", res.list, null, true);
      }));
    }
  }

  getPartnerContactsAsDropdown(partnerId: number, idAsValue?: boolean): Observable<SelectItem<PartnerContact | number>[]> {
    const url = `/contact/external/search?partner.id=${partnerId}`;
    if (idAsValue) {
      return this.http.get<QueryResult>(url).pipe(map(res => {
        return <SelectItem<number>[]>this.transform.convertToDropDown("fullName", res.list, 'id', true);
      }));
    } else {
      return this.http.get<QueryResult>(url).pipe(map(res => {
        return <SelectItem<PartnerContact>[]>this.transform.convertToDropDown("fullName", res.list, null, true);
      }));
    }
  }

  getTxnPdf(txnType: string, refId: number): Observable<any> {
    const pdfUrl = '/pdf';
    return this.http.get<any>(`${pdfUrl}?transaction=${txnType}&refId=${refId}`, {observe: 'response', responseType: BLOB_RESPONSE_TYPE});
  }

  getPaymentTermByPartnerId(partnerId: number): Observable<PartnerPaymentTerms> {
    const url = `/procurement/trading-partners/${partnerId}/payment-terms`;
    return this.http.get<PartnerPaymentTerms>(url);
  }

  saveAchDetail(partnerAchDetail): Observable<any> {
    const url = '/procurement/trading-partners/ach-detail';
    return this.http.post<any>(url, partnerAchDetail);

  }

  updateAchDetail(partnerAchDetail): Observable<any> {
    const url = `/procurement/trading-partners/ach-detail/${partnerAchDetail.id}`;
    return this.http.put<any>(url, partnerAchDetail);
  }

  deleteAchDetail(partnerAchDetail): Observable<any> {
    const url = `/procurement/trading-partners/ach-detail/${partnerAchDetail.id}`;
    return this.http.delete<any>(url);
  }

  updatePartnerPaymentSettings(partnerId: number, paymentSetting: PartnerPaymentSetting): Observable<any> {
    const url = `/procurement/trading-partners/${partnerId}/payment-settings/${paymentSetting.id}`;
    return this.http.put<any>(url, paymentSetting);
  }

  initiateMicroDepositVerification(achDetail): Observable<OrgAchDetail> {
    const url = '/ach/funding-source/initiate-org-micro-deposit';
    return this.http.post<OrgAchDetail>(url, achDetail);

  }

  verifyMicroDeposit(achDetail: PartnerAchDetail | OrgAchDetail, microDepositVerification: AchMicroDepositVerification): Observable<OrgAchDetail> {
    const url = `/ach/funding-source/verify-org-micro-deposit/${achDetail.id}`;
    return this.http.post<OrgAchDetail>(url, microDepositVerification);

  }


  getTasksSearch(urlString: string): Observable<QueryResult> {
    return this.http.get<QueryResult>(urlString);
  }

}
